import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import API from "./config/API";

function MaxFloorCountForm() {
  const navigate = useNavigate();
  const location = useLocation();

  // =====================================================
  // GET DATA FROM PREVIOUS PAGE
  // =====================================================

  const { fId, maxFloorCount: previousMaxFloorCount } = location.state || {};

  // =====================================================
  // FORM DATA
  // =====================================================

  const user = JSON.parse(localStorage.getItem("user"));

  const maxFloorCount = location.state?.maxFloorCount ?? 0;

  const [formData, setFormData] = useState({
    adminEmailId: user?.emailId || "",
    maxFloorCount: maxFloorCount,
  });

  const [existingCount, setExistingCount] = useState(
    previousMaxFloorCount ?? "",
  );

  const [popup, setPopup] = useState({
    message: "",
    type: "",
  });

  // =====================================================
  // LOAD PREVIOUS PAGE DATA
  // =====================================================

  useEffect(() => {
    if (!user?.emailId) {
      showPopup("User not logged in", "error");

      return;
    }

    setFormData((prev) => ({
      ...prev,
      adminEmailId: user.emailId,
    }));

    // Existing floor count comes from previous page
    if (previousMaxFloorCount !== undefined && previousMaxFloorCount !== null) {
      setExistingCount(Number(previousMaxFloorCount));
    }
  }, [previousMaxFloorCount]);

  // =====================================================
  // POPUP
  // =====================================================

  const showPopup = (message, type) => {
    setPopup({
      message,
      type,
    });

    setTimeout(() => {
      setPopup({
        message: "",
        type: "",
      });
    }, 2500);
  };

  // =====================================================
  // HANDLE CHANGE
  // =====================================================

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // =====================================================
  // SUBMIT
  // CREATE / UPDATE
  // =====================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.maxFloorCount) {
      showPopup("Max Floor Count is required", "error");

      return;
    }

    const count = Number(formData.maxFloorCount);

    if (count <= 0) {
      showPopup("Max Floor Count must be greater than 0", "error");

      return;
    }

    try {
      const payload = {
        // Existing ID from previous page
        fId: fId || null,

        adminEmailId: formData.adminEmailId,

        maxFloorCount: count,
      };

      console.log("Create/Update Max Floor Count Payload:", payload);

      const res = await axios.post(
        `${API.V1}/maxLimit/createMaxFloorCount`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",

            Authorization: `Bearer ${user?.jwtToken}`,
          },
        },
      );

      console.log("Max Floor Count Response:", res.data);

      if (res.data?.statusCode === 200) {
        showPopup(
          res.data?.message || "Floor count saved successfully!",
          "success",
        );

        // Update displayed existing count
        setExistingCount(count);

        // Clear input
        setFormData((prev) => ({
          ...prev,
          maxFloorCount: "",
        }));

        // Go back after save
        setTimeout(() => {
          navigate("/configurationSetUpCount");
        }, 1000);
      } else {
        showPopup(res.data?.message || "Failed to save floor count", "error");
      }
    } catch (err) {
      console.error("Max Floor Count Error:", err);

      if (err.response?.status === 401) {
        alert("Session expired. Please login again.");

        localStorage.clear();

        navigate("/");
      } else {
        showPopup(
          err.response?.data?.message ||
            "500 : Server down, Please try after sometime!",
          "error",
        );
      }
    }
  };

  // =====================================================
  // RETURN
  // =====================================================

  return (
    <div style={styles.container}>
      {/* =================================================
          POPUP
      ================================================= */}

      {popup.message && (
        <div
          style={{
            ...styles.popup,

            background:
              popup.type === "success"
                ? "linear-gradient(to right,#16a34a,#15803d)"
                : "linear-gradient(to right,#dc2626,#b91c1c)",
          }}
        >
          {popup.message}
        </div>
      )}

      <div style={styles.card}>
        {/* =================================================
            HEADER
        ================================================= */}

        <div style={styles.header}>
          <h2 style={styles.title}>Max Floor Count Setup</h2>

          <p style={styles.subtitle}>
            Configure maximum floor limit for your PG
          </p>
        </div>

        {/* =================================================
            EXISTING COUNT
        ================================================= */}

        <div style={styles.countBox}>
          <span style={styles.countLabel}>Existing Floor Count</span>

          <div style={styles.countValue}>
            {existingCount !== "" ? existingCount : "0"}
          </div>
        </div>

        {/* =================================================
            FORM
        ================================================= */}

        <form onSubmit={handleSubmit}>
          {/* EMAIL */}

          <div style={styles.formGroup}>
            <label style={styles.label}>Admin Email ID</label>

            <input
              type="text"
              name="adminEmailId"
              value={formData.adminEmailId}
              readOnly
              style={styles.readOnlyInput}
            />
          </div>

          {/* FLOOR COUNT */}

          <div style={styles.formGroup}>
            <label style={styles.label}>Enter Max Floor Count</label>

            <input
              type="number"
              name="maxFloorCount"
              value={formData.maxFloorCount}
              onChange={handleChange}
              placeholder="Enter floor count"
              min="1"
              style={styles.input}
            />
          </div>

          {/* BUTTON */}

          <button type="submit" style={styles.button}>
            {fId ? "Update Floor Count" : "Save Floor Count"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default MaxFloorCountForm;

// =====================================================
// STYLES
// =====================================================

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "linear-gradient(to right,#dbeafe,#f8fafc)",
    padding: "20px",
    fontFamily: "Arial,sans-serif",
  },

  card: {
    width: "450px",
    background: "#ffffff",
    borderRadius: "22px",
    padding: "35px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
  },

  header: {
    textAlign: "center",
    marginBottom: "25px",
  },

  title: {
    fontSize: "30px",
    fontWeight: "bold",
    color: "#0f172a",
    marginBottom: "8px",
  },

  subtitle: {
    color: "#64748b",
    fontSize: "15px",
  },

  countBox: {
    background: "linear-gradient(to right,#0ea5e9,#2563eb)",
    padding: "18px",
    borderRadius: "16px",
    marginBottom: "25px",
    textAlign: "center",
    color: "#ffffff",
  },

  countLabel: {
    display: "block",
    fontSize: "15px",
    marginBottom: "10px",
    fontWeight: "600",
  },

  countValue: {
    fontSize: "42px",
    fontWeight: "bold",
  },

  formGroup: {
    marginBottom: "20px",
  },

  label: {
    display: "block",
    marginBottom: "8px",
    color: "#1e293b",
    fontWeight: "bold",
    fontSize: "15px",
  },

  input: {
    width: "100%",
    padding: "14px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    outline: "none",
    fontSize: "16px",
    background: "#f8fafc",
    boxSizing: "border-box",
  },

  readOnlyInput: {
    width: "100%",
    padding: "14px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    outline: "none",
    fontSize: "15px",
    background: "#e2e8f0",
    color: "#0f172a",
    fontWeight: "600",
    boxSizing: "border-box",
  },

  button: {
    width: "100%",
    padding: "15px",
    background: "linear-gradient(to right,#2563eb,#1d4ed8)",
    color: "#ffffff",
    border: "none",
    borderRadius: "14px",
    fontSize: "17px",
    fontWeight: "bold",
    cursor: "pointer",
    marginTop: "10px",
  },

  popup: {
    position: "fixed",
    top: "20px",
    right: "20px",
    color: "#ffffff",
    padding: "14px 20px",
    borderRadius: "10px",
    fontWeight: "bold",
    zIndex: 999,
    boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
  },
};
